// src/components/PermGate.jsx
// FINAL CLEANED - COPY/PASTE READY
// ✅ RBAC compatible
// ✅ No flicker while auth booting
// ✅ Supports auth roles + rbacRoles
// ✅ Supports perm, role, or both together
// ✅ Safe defaults
// ✅ No hook-order issues

import React, { useMemo } from "react";
import { useAuth } from "../context/AuthContext";

function norm(v) {
  return String(v || "").trim().toLowerCase();
}

function toArray(v) {
  if (Array.isArray(v)) return v;
  if (v == null || v === "") return [];
  return [v];
}

export default function PermGate({
  perm,
  mode = "any",
  role,
  roleMode = "any",
  children,
  fallback = null,
  loadingFallback = null,
}) {
  const auth = useAuth() || {};

  const {
    hasPerm = () => false,
    hasRole = () => false,
    booting = false,
    rbacRoles = [],
    isAuthenticated = false,
  } = auth;

  const rbacSet = useMemo(() => {
    return new Set((rbacRoles || []).map(norm));
  }, [rbacRoles]);

  const permList = useMemo(() => toArray(perm), [perm]);
  const roleList = useMemo(() => toArray(role), [role]);

  const hasAnyRole = useMemo(() => {
    return (roles) =>
      roles.some((r) => {
        const rr = norm(r);
        return hasRole(rr) || rbacSet.has(rr);
      });
  }, [hasRole, rbacSet]);

  const hasAllRole = useMemo(() => {
    return (roles) =>
      roles.every((r) => {
        const rr = norm(r);
        return hasRole(rr) || rbacSet.has(rr);
      });
  }, [hasRole, rbacSet]);

  const hasAnyPerm = useMemo(() => {
    return (perms) => perms.some((p) => hasPerm(String(p)));
  }, [hasPerm]);

  const hasAllPerm = useMemo(() => {
    return (perms) => perms.every((p) => hasPerm(String(p)));
  }, [hasPerm]);

  if (booting) {
    return loadingFallback;
  }

  let roleAllowed = true;
  let permAllowed = true;

  if (roleList.length > 0) {
    roleAllowed =
      roleMode === "all" ? hasAllRole(roleList) : hasAnyRole(roleList);
  }

  if (permList.length > 0) {
    permAllowed =
      mode === "all" ? hasAllPerm(permList) : hasAnyPerm(permList);
  }

  // If neither role nor perm is provided:
  // - allow authenticated users only
  const allowed =
    roleList.length === 0 && permList.length === 0
      ? !!isAuthenticated
      : roleAllowed && permAllowed;

  return allowed ? children : fallback;
}